import { downloadFile, requestJson } from "@/lib/api";

export interface CustomerOption { id: string; name: string; clientType: string | null; }
export interface UnitOption { id: string; name: string; }

export interface NegotiationOption {
  conditionId: string;
  scopeType: "CLIENTE" | "UNIDADE_ENTREGA";
  deliveryUnitId: string | null;
  validFrom: string;
  validTo: string | null;
  status: "FUTURA" | "VIGENTE" | "ENCERRADA";
  hasApprovedTable: boolean;
  approvedVersionNumber: number | null;
  label: string;
}

export interface ResolvedPercent { value: number | null; origin: string; explicitZero: boolean; }

export interface NegotiationSummary {
  configured: boolean;
  message: string | null;
  clientCommissionPercent: ResolvedPercent;
  intermediaryCommissionPercent: ResolvedPercent;
  intermediaryCommissionBase: string | null;
  intermediaryIncompleteAfterClientCommission: boolean;
  targetMarginPercent: ResolvedPercent;
  minimumMarginPercent: ResolvedPercent;
  taxListOrigin: string;
  taxListState: string;
}

export interface OrderDeliveryExpense { description: string; scope: string; fixedValue: number | null; }

export interface ProfitabilityRow {
  productId: string;
  code: number | null;
  productName: string;
  presentationUnitId: string;
  presentationUnitName: string;
  cutName: string | null;
  salePrice: number | null;        // PREÇO DE VENDA: valor final após as regras oficiais, inclusive o piso
  registeredPrice: number | null;  // PREÇO CADASTRADO: valor da tabela antes do piso (próprio ou herdado)
  priceFloorApplied: boolean;      // true = o preço de venda subiu em relação ao cadastrado por causa do piso
  rawCost: number | null;
  rawCostUnitName: string | null;
  mermaSurcharge: number | null;
  operationalCost: number | null;
  packagingCost: number | null;
  costValue: number | null;        // custo composto exibido (completo ou parcial); null = indisponível
  costComplete: boolean;           // true = custo integral; false = parcial
  clientCommissionValue: number | null;
  intermediaryCommissionValue: number | null;
  taxesUnitValue: number | null;
  resultValue: number | null;      // DEFINITIVO: só quando conclusive; null caso contrário
  marginPercent: number | null;    // DEFINITIVO: só quando conclusive
  marginStatus: "ACIMA_META" | "ENTRE" | "ABAIXO_MINIMA" | null;
  partialResultValue: number | null;   // PARCIAL: quando partial; null caso contrário
  partialMarginPercent: number | null; // PARCIAL: quando partial
  resultLabel: "MARGEM_FINAL" | "ANTES_DESPESAS_PEDIDO_ENTREGA";
  conclusive: boolean;             // todos os fatores presentes (comportamento atual)
  partial: boolean;                // valores calculados com fatores faltantes → não definitivos
  reasons: string[];
}

export interface ProfitabilityResponse {
  customerId: string;
  customerName: string;
  unitId: string | null;
  unitName: string | null;
  date: string;
  dataCaveat: string;
  negotiation: NegotiationSummary;
  orderDeliveryExpenses: OrderDeliveryExpense[];
  rows: ProfitabilityRow[];
  page: number;
  size: number;
  totalRows: number;
  selectedNegotiationId: string | null;
  negotiationScope: string | null;
  negotiationValidFrom: string | null;
  negotiationValidTo: string | null;
  negotiationStatus: string | null;
  priceTableRegistered: boolean;
  priceTableVersionNumber: number | null;
  priceSource: "NEGOCIACAO_CONGELADA" | "ATUAL";
}

const BASE = "/api/v1/admin/profitability";

export function fetchProfitabilityCustomers(): Promise<CustomerOption[]> {
  return requestJson<CustomerOption[]>(`${BASE}/customers`, { method: "GET" });
}

export function fetchProfitabilityUnits(customerId: string): Promise<UnitOption[]> {
  return requestJson<UnitOption[]>(`${BASE}/customers/${customerId}/delivery-units`, { method: "GET" });
}

export function fetchProfitabilityNegotiations(customerId: string, unitId?: string | null): Promise<NegotiationOption[]> {
  const q = new URLSearchParams();
  q.set("customerId", customerId);
  if (unitId) q.set("unitId", unitId);
  return requestJson<NegotiationOption[]>(`${BASE}/negotiations?${q.toString()}`, { method: "GET" });
}

export function fetchProfitability(params: {
  customerId: string;
  unitId?: string | null;
  date?: string | null;
  negotiationId?: string | null;
  query?: string;
  page?: number;
  size?: number;
}): Promise<ProfitabilityResponse> {
  const q = new URLSearchParams();
  q.set("customerId", params.customerId);
  if (params.unitId) q.set("unitId", params.unitId);
  if (params.date) q.set("date", params.date);
  if (params.negotiationId) q.set("negotiationId", params.negotiationId);
  if (params.query) q.set("query", params.query);
  if (params.page != null) q.set("page", String(params.page));
  if (params.size != null) q.set("size", String(params.size));
  return requestJson<ProfitabilityResponse>(`${BASE}?${q.toString()}`, { method: "GET" });
}

/**
 * Baixa o XLSX da Rentabilidade com os MESMOS filtros da tela (cliente, unidade, negociação/versão, data,
 * busca). A planilha é gerada pelo backend com os mesmos preços/cálculos exibidos — nenhum cálculo no FE.
 * Gated no backend pela permissão de visualização (RELATORIOS.RENTABILIDADE.VISUALIZAR).
 */
export function exportProfitabilityXlsx(params: {
  customerId: string;
  unitId?: string | null;
  date?: string | null;
  negotiationId?: string | null;
  query?: string;
}): Promise<void> {
  const q = new URLSearchParams();
  q.set("customerId", params.customerId);
  if (params.unitId) q.set("unitId", params.unitId);
  if (params.date) q.set("date", params.date);
  if (params.negotiationId) q.set("negotiationId", params.negotiationId);
  if (params.query) q.set("query", params.query);
  return downloadFile(`${BASE}/export.xlsx?${q.toString()}`, "rentabilidade.xlsx");
}

/** Rótulos legíveis dos motivos de incompletude. */
export const REASON_LABELS: Record<string, string> = {
  CUSTO_NAO_CADASTRADO: "Custo não cadastrado",
  APRESENTACAO_SEM_CUSTO: "Apresentação sem custo",
  CUSTO_BASE_INCOMPLETO: "Custo base incompleto",
  MATERIA_PRIMA_AUSENTE: "Matéria-prima não cadastrada",
  OPERACIONAL_AUSENTE: "Custo operacional não cadastrado",
  OPERACIONAL_SEM_VALOR: "Custo operacional sem valor/percentual",
  ORIGEM_AUSENTE: "Matéria-prima de origem não vinculada",
  ORIGEM_MULTIPLA: "Múltiplas matérias-primas de origem",
  ORIGEM_INATIVA: "Matéria-prima de origem inativa",
  ORIGEM_SEM_CUSTO: "Matéria-prima de origem sem custo",
  ORIGEM_INVALIDA: "Vínculo de matéria-prima de origem inválido",
  EMBALAGEM_INCOMPLETA: "Embalagem incompleta",
  UNIDADES_INCOMPATIVEIS: "Unidade da apresentação incompatível com o custo",
  COMPOSICAO_NAO_CADASTRADA: "Composição de embalagem não cadastrada",
  MATERIAL_SEM_CUSTO: "Material de embalagem sem custo",
  MATERIAL_INATIVO: "Material de embalagem inativo",
  NEGOCIACAO_NAO_CADASTRADA: "Negociação não cadastrada",
  COMISSAO_CLIENTE_NAO_CONFIGURADA: "Comissão do cliente não configurada",
  INTERMEDIARIO_NAO_CONFIGURADO: "Comissão do intermediário não configurada",
  INTERMEDIARIO_INCOMPLETO: "Intermediário incompleto (base após comissão do cliente sem comissão)",
  IMPOSTOS_NAO_CONFIGURADOS: "Impostos/despesas não configurados",
  IMPOSTO_BASE_INCOMPLETA: "Base de imposto incompleta",
  DESPESA_FIXA_UNIDADE_INCOMPATIVEL: "Despesa fixa por produto com unidade incompatível",
  PRECO_ZERO: "Preço zero (margem percentual não aplicável)",
  PRECO_AUSENTE: "Preço ausente",
};
